import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import posthog from './posthogClient';
import { EVENTS, PageLoadEventProps } from './events';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { v4 as uuidv4 } from 'uuid';

const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
const SELISEAI_USER_SESSION_ID = "seliseai_user_session_id"
const SELISEAI_TAB_COUNT = "seliseai_tab_count"

const getDeviceType = () => {
    return /Mobi|Android/i.test(navigator.userAgent)
    ? 'mobile'
    : /Tablet|iPad/i.test(navigator.userAgent)
    ? 'tablet'
    : 'desktop';
}

export const trackEvent = (event: any) => {
  try {
    posthog.capture(event.name, event.properties);
  } catch (error) {
    console.error('Error tracking event: ', error);
  }
};

export const trackException = (error: any, additionalInfo: any) => {
  try {
    posthog.captureException(error, additionalInfo);
  } catch (error) {
    console.error('Error tracking exception: ', error);
  }
};

export const trackPageLoadComplete = (pagePath: string, userId: string) => {
    if(typeof window === 'undefined' || !('performance' in window)) return;
    const performance = window.performance;

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if(!navigation) return;

    const paintEntries = performance.getEntriesByType('paint') as PerformanceEntry[];
    const firstContentfulPaintTime = paintEntries.find(e => e.name === 'first-contentful-paint')?.startTime ?? 0;
    paintEntries.forEach((e) => {
        if(e.name === 'first-contentful-paint') {
            console.log('first-contentful-paint', e.startTime)
        }
    })
    const loadTime = navigation.loadEventEnd - navigation.startTime;    
    const domContentLoadedTime = navigation.domContentLoadedEventEnd - navigation.startTime;
    const timeToInteractiveTime = navigation.domInteractive - navigation.startTime;

    const connectionType = (navigator as any).connection?.effectiveType || 'unknown';

    const event: PageLoadEventProps = {
        loadTimeMs: loadTime,
        domContentLoadedTimeMs: domContentLoadedTime,
        firstContentfulPaintTimeMs: firstContentfulPaintTime,
        timeToInteractiveTimeMs: timeToInteractiveTime,
        pagePath: pagePath,
        userId: userId,
        deviceType: getDeviceType(),
        connectionType: connectionType,
        timestamp: new Date().toISOString(),
    };

    trackEvent(EVENTS.PAGE_LOAD_COMPLETE(event));
}

export const usePageTracking = () => {
    const location = useLocation();
    useEffect(() => {
        const path = location.pathname + location.search;
        trackEvent(EVENTS.PAGE_VIEW(path));
    }, [location.pathname]);
};

export const useTrackPageLoadComplete = () => {
    const location = useLocation();
    const user = useCommonStore((state) => state.user);

    useEffect(() => {
        const path = location.pathname;
        trackPageLoadComplete(path, user?.id || '');
    }, [location.pathname]);
};

const getOrCreateSessionId = () => {
    let isExistingSession = true;
    let id = localStorage.getItem(SELISEAI_USER_SESSION_ID);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(SELISEAI_USER_SESSION_ID, id);
      isExistingSession = false;
    }
    return {id, isExistingSession};
  };

export const useSessionTracker = () => {
  const user = useCommonStore((state) => state.user);
  const location = useLocation();

  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(SELISEAI_USER_SESSION_ID));
  const sessionIdRef = useRef<string | null>(sessionId);
  const startTimeRef = useRef<number>(0);
  const lastActivityRef = useRef<number>(Date.now());
  const activeTimeRef = useRef<number>(0);
  const pagesVisitedRef = useRef<string[]>([]);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isIdleRef = useRef<boolean>(false);
  const hasEndedRef = useRef<boolean>(false);
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(()=> {
    sessionIdRef.current = sessionId;
  }, [sessionId])

  useEffect(() => {
    if (user?.id) lastUserIdRef.current = user.id;
  }, [user?.id]);

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  const startIdleTimer = () => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      triggerIdleEvent();
    }, IDLE_THRESHOLD_MS);
  };

  const triggerIdleEvent =() => {
    const sessionId = sessionIdRef.current;
    const userId = lastUserIdRef.current;
    if (!sessionId || !userId) return;

    const now = Date.now();
    const idleDuration = (now - lastActivityRef.current) / 1000;

    trackEvent(
      EVENTS.USER_IDLE({
        idleDurationSeconds: Math.round(idleDuration),
        idleThresholdSeconds: IDLE_THRESHOLD_MS / 1000,
        lastActivePage: window.location.pathname,
        sessionId,
        userId: userId,
        totalActiveTimeSeconds: Math.round(activeTimeRef.current / 1000),
        timestamp: new Date().toISOString(),
      })
    );

    isIdleRef.current = true;
  };

  const handleUserActivity = () => {
    const now = Date.now();
    // If user returns from idle
    if (isIdleRef.current) {
      isIdleRef.current = false;
    }
    // Add elapsed since last activity to active time
    activeTimeRef.current += now - lastActivityRef.current;
    lastActivityRef.current = now;
    startIdleTimer();
  };

  useEffect(() => {
    if (!user?.id || typeof window === "undefined") return;

    const {id, isExistingSession} = getOrCreateSessionId();
    setSessionId(id);
    startTimeRef.current = Date.now();
    lastActivityRef.current = Date.now();
    hasEndedRef.current = false;

    if(!isExistingSession) {
      trackEvent(
        EVENTS.SESSION_STARTED({
          sessionId: id,
          userId: user.id,
          entryPage: location.pathname,
          referrer: document.referrer || "direct",
          deviceType: getDeviceType(),
          timestamp: new Date().toISOString(),
        })
      );
    }

    startIdleTimer();

    window.addEventListener("mousemove", handleUserActivity);
    window.addEventListener("click", handleUserActivity);
    window.addEventListener("keypress", handleUserActivity);

    return () => {
      window.removeEventListener("mousemove", handleUserActivity);
      window.removeEventListener("click", handleUserActivity);
      window.removeEventListener("keypress", handleUserActivity);
    };
  }, [user?.id]);

  useEffect(() => {
    pagesVisitedRef.current.push(location.pathname);
  }, [location.pathname]);

  const endSession = (reason: string) => {
    if (hasEndedRef.current) return;

    const userId = user?.id || lastUserIdRef.current;
    const sessionId = sessionIdRef.current;

    if (!sessionId || !userId) return;

    hasEndedRef.current = true;
    clearIdleTimer();

    const endTime = Date.now();
    const totalDuration = (endTime - startTimeRef.current) / 1000;
    const activeDuration = activeTimeRef.current / 1000;

    trackEvent(
      EVENTS.SESSION_ENDED({
        sessionId,
        userId,
        sessionDurationSeconds: Math.round(totalDuration),
        activeTimeSeconds: Math.round(activeDuration),
        pagesVisited: pagesVisitedRef.current.length,
        exitPage: window.location.pathname,
        endReason: reason,
        timestamp: new Date().toISOString(),
      })
    );
    setSessionId(null);
    localStorage.removeItem(SELISEAI_USER_SESSION_ID);
  };

  const incrementTabCount = () => {
    const tabCount = localStorage.getItem(SELISEAI_TAB_COUNT);
    if(tabCount) {
      localStorage.setItem(SELISEAI_TAB_COUNT, (parseInt(tabCount) + 1).toString());
    } else {
      localStorage.setItem(SELISEAI_TAB_COUNT, "1");
    }
  }

  const decrementTabCount = () => {
    const tabCount = localStorage.getItem(SELISEAI_TAB_COUNT);
    if(tabCount) {
      if(parseInt(tabCount) === 1) {
        localStorage.removeItem(SELISEAI_TAB_COUNT);
        endSession("tab_close");
      } else {
        localStorage.setItem(SELISEAI_TAB_COUNT, (parseInt(tabCount) - 1).toString());
      }
    }
  }

  useEffect(() => {
    if(sessionId && user?.id) {
        startIdleTimer();
    }
    const handleUnload = () => decrementTabCount();

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [sessionId, user?.id]);

  useEffect(() => {
    if (!user && sessionId && lastUserIdRef.current) {
      endSession("logout");
    }
  }, [user]);

  useEffect(() => {
    incrementTabCount();

    return () => {
      decrementTabCount();
    };
  }, []);
};
